import { useState } from 'react';
import type { ChatSessionDto } from '@aiai/shared';
import { MessageCircle, Plus, Trash2 } from 'lucide-react';
import { formatRelative } from '../lib/format';
import { personLabel, useViewerEmail } from '../lib/people';
import { ConfirmDialog } from './ConfirmDialog';
import { Spinner } from './Spinner';

/**
 * The conversation-history rail of the chat page: "New chat" on top, then the
 * project's sessions ordered by last activity. `activeSessionId` is null in
 * draft mode (a "New chat" not yet sent — no row exists server-side until the
 * first message). Delete is hover-revealed per session and confirmed in a
 * dialog; the parent owns the delete mutation.
 */
export function ChatSessionRail({
  sessions,
  activeSessionId,
  isDraft,
  isLoading,
  readOnly,
  deleteBusy,
  deleteError,
  onSelect,
  onNewChat,
  onDelete,
}: {
  sessions: ChatSessionDto[];
  activeSessionId: string | null;
  /** True when a fresh "New chat" (no session yet) is open. */
  isDraft: boolean;
  isLoading: boolean;
  readOnly: boolean;
  deleteBusy: boolean;
  deleteError: string | null;
  onSelect: (sessionId: string) => void;
  onNewChat: () => void;
  onDelete: (sessionId: string) => Promise<void>;
}) {
  const [confirmDelete, setConfirmDelete] = useState<ChatSessionDto | null>(null);
  const viewerEmail = useViewerEmail();

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {!readOnly && (
        <button
          type="button"
          className="btn-secondary w-full justify-center"
          onClick={onNewChat}
          disabled={isDraft}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          New chat
        </button>
      )}

      <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto pr-0.5">
        {isDraft && (
          <div className="rounded-lg bg-surface-2 px-3 py-2">
            <p className="truncate text-sm font-medium text-ink">New conversation</p>
            <p className="text-2xs text-ink-faint">Not sent yet</p>
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center py-6">
            <Spinner className="h-4 w-4" />
          </div>
        )}

        {!isLoading && sessions.length === 0 && !isDraft && (
          <div className="flex flex-col items-center gap-1.5 px-2 py-8 text-center">
            <MessageCircle className="h-5 w-5 text-ink-faint" aria-hidden="true" />
            <p className="text-xs text-ink-faint">No conversations yet.</p>
          </div>
        )}

        {sessions.map((s) => {
          const active = s.id === activeSessionId;
          return (
            <div
              key={s.id}
              className={`group relative rounded-lg transition-colors ${
                active ? 'bg-surface-2' : 'hover:bg-surface-2/60'
              }`}
            >
              <button
                type="button"
                className="block w-full px-3 py-2 text-left"
                onClick={() => onSelect(s.id)}
              >
                <p
                  className={`truncate pr-6 text-sm ${
                    active ? 'font-medium text-ink' : 'text-ink-muted'
                  }`}
                >
                  {s.title || 'New conversation'}
                </p>
                <p className="truncate text-2xs text-ink-faint">
                  {s.startedByEmail && (
                    <span title={`Started by ${s.startedByEmail}`}>
                      by {personLabel(s.startedByEmail, viewerEmail)} ·{' '}
                    </span>
                  )}
                  {formatRelative(s.lastMessageAt)}
                </p>
              </button>
              {!readOnly && (
                <button
                  type="button"
                  className="absolute right-2 top-2 rounded p-1 text-ink-faint opacity-0 transition-opacity hover:bg-surface hover:text-ink focus-visible:opacity-100 group-hover:opacity-100"
                  aria-label={`Delete conversation "${s.title || 'New conversation'}"`}
                  onClick={() => setConfirmDelete(s)}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete conversation?"
        message={
          <>
            <span className="font-medium text-ink">
              “{confirmDelete?.title || 'New conversation'}”
            </span>{' '}
            and all its messages will be permanently deleted. The PM keeps no
            memory of deleted conversations.
          </>
        }
        busy={deleteBusy}
        error={deleteError}
        onConfirm={() => {
          if (!confirmDelete) return;
          void onDelete(confirmDelete.id).then(() => setConfirmDelete(null));
        }}
        onClose={() => setConfirmDelete(null)}
      />
    </div>
  );
}
